import { AlertTriangle, Info, Plus, X } from 'lucide-react';
import {
  AGENTS,
  DEFAULT_AGENT,
  TIMING_BANDS,
  WINDOW_DAYS,
  persona as catalogPersona,
  realize,
  slotHoursWithin,
  workflowFitsAudience,
  type SequenceStep,
  type StepSlot,
  type TimingBand,
  type WindowDay,
} from '@vagusx/shared';
import type { WorkflowOption } from '@vagusx/clients';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { Field } from './Field';
import type { StepContext } from './StepCard';
import {
  BAND_LABELS,
  addSlot,
  describeDays,
  removeSlot,
  setSlotBand,
  setSlotHours,
  setWhenToCall,
  slotHours,
  stepCallHours,
  stepDays,
  toggleStepDay,
  whenToCall,
  type StepHours,
} from './spec';

/**
 * The step controls added by ADR-318 (G): which workflow a step runs, when its
 * calls go out, and — for VagusX ops — which stack carries them.
 */

/** Why a workflow cannot be picked for this audience, or null when it can. */
export function workflowUnavailableReason(
  option: WorkflowOption,
  audienceType: StepContext['audienceType']
): string | null {
  // The server's rule (R11), shared so the picker and publish never disagree.
  if (!workflowFitsAudience(audienceType, option.intent)) {
    return `made for ${(option.intent ?? '').replace(/_/g, ' ')}, not for a referral`;
  }
  return null;
}

export function WorkflowField({
  step,
  context,
  onChange,
}: {
  step: SequenceStep;
  context: StepContext;
  onChange: (step: SequenceStep) => void;
}): React.ReactElement | null {
  if (step.channel !== 'voice') return null;
  const options = context.workflowOptions;
  const selectedId = step.workflow?.workflowDefnId ?? '';
  // Only a workflow the step HAS can be "no longer published"; none yet is not an error.
  const selectedListed = !step.workflow || (options?.some((o) => o.id === selectedId) ?? true);
  const pin = context.pins.get(step.id);

  return (
    <Field
      label="Workflow"
      htmlFor={`step-workflow-${step.id}`}
      hint="From Setup → Workflows. Publishing the campaign locks in the version published then."
    >
      <Select
        value={selectedListed ? selectedId : ''}
        onValueChange={(workflowDefnId) =>
          workflowDefnId !== selectedId &&
          onChange({ ...step, workflow: { workflowDefnId, versionId: null } })
        }
      >
        <SelectTrigger id={`step-workflow-${step.id}`} className="w-80">
          <SelectValue placeholder={options ? 'Choose a workflow' : 'Loading workflows…'} />
        </SelectTrigger>
        <SelectContent>
          {(options ?? []).map((option) => {
            // Offered but not selectable, with the reason: hiding it would read
            // as "the workflow is missing" rather than "not for this audience".
            const reason = workflowUnavailableReason(option, context.audienceType);
            return (
              <SelectItem key={option.id} value={option.id} disabled={!!reason}>
                {option.name}
                {reason ? ` — ${reason}` : ` · v${option.activeVersion.version}`}
              </SelectItem>
            );
          })}
          {options?.length === 0 && (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              No published outbound call workflow yet.
            </div>
          )}
        </SelectContent>
      </Select>

      {!selectedListed && (
        <p className="flex w-full items-start gap-1.5 text-xs text-destructive">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          The workflow this step ran is no longer published. Choose another before publishing.
        </p>
      )}
      {pin && pin.latestVersion !== null && pin.latestVersion > pin.pinnedVersion && (
        <p className="flex w-full items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3 w-3 shrink-0" />
          Version {pin.latestVersion} of this workflow is published; this campaign runs version{' '}
          {pin.pinnedVersion}. Publishing the campaign moves new patients onto it.
        </p>
      )}
    </Field>
  );
}

/**
 * "When to call: Automatic / Defined", writing `step.timing` (ADR-318,
 * ADR-348). Automatic calls in the late-morning and late-afternoon slots;
 * Defined narrows to some of the campaign's days and to up to four slots of
 * its own, each a preset part of the day or exact hours typed in. It can never
 * add a day the campaign does not call on, and hours that leave no time inside
 * the calling hours are refused at publish.
 */
export function WhenToCallField({
  step,
  campaignDays,
  callingHours,
  onChange,
}: {
  step: SequenceStep;
  campaignDays: readonly WindowDay[];
  callingHours: StepHours;
  onChange: (step: SequenceStep) => void;
}): React.ReactElement {
  const mode = whenToCall(step);
  const days = stepDays(step, campaignDays);
  const ranges = (hours: StepHours[]): string =>
    hours.map((h) => `${h.start}–${h.end}`).join(' or ');
  const { hours, fallsBack } = stepCallHours(step, callingHours);
  const hint =
    mode === 'auto'
      ? `${ranges(hours)}, when people most often pick up, on any calling day. A try is moved off the slot the last one used when another opens later that day.`
      : mode === 'defined'
        ? hours.length > 0
          ? `Only ${ranges(hours)}, on ${describeDays(days)}.`
          : 'None of these hours is inside the calling hours, so this step cannot call.'
        : `Set before this choice existed: it ${
            step.timeOfDay === 'rotate'
              ? 'varies the time of day'
              : 'calls at the start of calling hours'
          }${step.window ? ', in hours of its own' : ''}. Choose one to change it.`;

  return (
    <Field label="When to call" hint={hint}>
      <ToggleGroup
        type="single"
        variant="outline"
        size="sm"
        aria-label="When to call"
        value={mode === 'legacy' ? '' : mode}
        onValueChange={(next) =>
          (next === 'auto' || next === 'defined') && onChange(setWhenToCall(step, next))
        }
      >
        <ToggleGroupItem value="auto">Automatic</ToggleGroupItem>
        <ToggleGroupItem value="defined">Defined</ToggleGroupItem>
      </ToggleGroup>

      {step.timing?.mode === 'defined' && (
        <div className="w-full space-y-2 rounded-md border bg-muted/30 p-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="w-10 text-xs text-muted-foreground">Days</span>
            <ToggleGroup
              type="multiple"
              variant="outline"
              size="sm"
              aria-label="Days this step calls on"
              value={days}
              onValueChange={(next) => {
                // One click changes one day; find it and let the helper decide
                // (it refuses the last day and any day the campaign does not call on).
                const day = WINDOW_DAYS.find((d) => next.includes(d) !== days.includes(d));
                if (day) onChange(toggleStepDay(step, day, campaignDays));
              }}
            >
              {WINDOW_DAYS.map((day) => (
                <ToggleGroupItem
                  key={day}
                  value={day}
                  disabled={!campaignDays.includes(day)}
                  aria-label={day}
                  className={SELECTED}
                  title={
                    campaignDays.includes(day) ? undefined : 'Not a calling day for this campaign'
                  }
                >
                  {day.slice(0, 2).replace(/^./, (c) => c.toUpperCase())}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>

          {step.timing.slots.map((slot, index, all) => (
            <SlotRow
              key={index}
              step={step}
              slot={slot}
              index={index}
              count={all.length}
              callingHours={callingHours}
              fallsBack={fallsBack}
              onChange={onChange}
            />
          ))}

          {fallsBack && (
            <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <Info className="mt-0.5 h-3 w-3 shrink-0" />
              None of these parts of the day is inside the calling hours ({callingHours.start}–
              {callingHours.end}), so this step calls at any time in them.
            </p>
          )}

          {addSlot(step) !== step && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => onChange(addSlot(step))}
            >
              <Plus className="mr-1 h-3 w-3" />
              Add a time slot
            </Button>
          )}
        </div>
      )}
    </Field>
  );
}

/**
 * One slot of a defined step: its hours, typed or from a preset. Presets stay
 * bands, not copied hours, so changing the campaign's calling hours later
 * moves them too. Typing a time is what makes the slot's hours its own.
 */
function SlotRow({
  step,
  slot,
  index,
  count,
  callingHours,
  fallsBack,
  onChange,
}: {
  step: SequenceStep;
  slot: StepSlot;
  index: number;
  count: number;
  callingHours: StepHours;
  /** No slot fits the calling hours, so the step calls at any time in them. */
  fallsBack: boolean;
  onChange: (step: SequenceStep) => void;
}): React.ReactElement {
  const hours = slotHours(slot, callingHours);
  const label = count > 1 ? `Slot ${index + 1}` : 'Time';
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="w-10 text-xs text-muted-foreground">{label}</span>
        <div className="flex items-center gap-2">
          <Input
            type="time"
            className="h-8 w-32 bg-background"
            aria-label={`${label}: calls from`}
            value={hours?.start ?? ''}
            onChange={(e) =>
              e.target.value &&
              onChange(
                setSlotHours(step, index, {
                  start: e.target.value,
                  end: hours?.end ?? callingHours.end,
                })
              )
            }
          />
          <span className="text-sm text-muted-foreground">to</span>
          <Input
            type="time"
            className="h-8 w-32 bg-background"
            aria-label={`${label}: calls until`}
            value={hours?.end ?? ''}
            onChange={(e) =>
              e.target.value &&
              onChange(
                setSlotHours(step, index, {
                  start: hours?.start ?? callingHours.start,
                  end: e.target.value,
                })
              )
            }
          />
        </div>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          aria-label={`${label}: part of the day`}
          value={slot.band === 'custom' ? '' : slot.band}
          onValueChange={(band) =>
            (TIMING_BANDS as readonly string[]).includes(band) &&
            onChange(setSlotBand(step, index, band as TimingBand))
          }
        >
          {TIMING_BANDS.map((band) => (
            <ToggleGroupItem key={band} value={band} className={cn(SELECTED, 'px-2.5 text-xs')}>
              {BAND_LABELS[band]}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {count > 1 && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label={`Remove ${label.toLowerCase()}`}
            onClick={() => onChange(removeSlot(step, index))}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <HoursProblem slot={slot} callingHours={callingHours} />
      {slot.band !== 'custom' && !hours && !fallsBack && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3 w-3 shrink-0" />
          {BAND_LABELS[slot.band]} is outside the calling hours ({callingHours.start}–
          {callingHours.end}), so this slot is not used.
        </p>
      )}
    </div>
  );
}

/**
 * A chosen day or preset reads as chosen. The toggle's own "on" tint is close
 * enough to "off" that a step's days could not be read at a glance.
 */
const SELECTED = 'data-[state=on]:bg-highlight data-[state=on]:text-highlight-foreground';

/**
 * Why a slot's typed hours will not work, said where they are typed rather
 * than at publish. Judged against the hours calls can actually go out (the
 * campaign's, inside the organization's quiet hours) with the resolver's own
 * rule, so the editor and the launch check agree.
 */
function HoursProblem({
  slot,
  callingHours,
}: {
  slot: StepSlot;
  callingHours: StepHours;
}): React.ReactElement | null {
  if (slot.band !== 'custom' || !slot.hours) return null;
  const { hours } = slot;
  const calling = `${callingHours.start}–${callingHours.end}`;
  const problem =
    hours.start >= hours.end
      ? 'The end time must be after the start.'
      : slotHoursWithin(slot, callingHours) === null
        ? `These hours are outside the hours calls can go out (${calling}), so the campaign cannot launch.`
        : null;
  const trimmed =
    !problem && (hours.start < callingHours.start || hours.end > callingHours.end)
      ? `Calls only go out inside ${calling}, the calling hours within the practice's quiet hours.`
      : null;
  if (!problem && !trimmed) return null;
  return (
    <p
      className={cn(
        'flex items-start gap-1.5 text-xs',
        problem ? 'text-destructive' : 'text-muted-foreground'
      )}
    >
      {problem ? (
        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
      ) : (
        <Info className="mt-0.5 h-3 w-3 shrink-0" />
      )}
      {problem ?? trimmed}
    </p>
  );
}

/**
 * Which stack carries this step's calls (P4). VagusX ops only: a practice
 * chooses who speaks (the campaign's voice), never the plumbing. A stack that
 * cannot speak as the campaign's voice is shown and disabled, with the reason.
 */
export function StackField({
  step,
  personaId,
  onChange,
}: {
  step: SequenceStep;
  personaId: string;
  onChange: (step: SequenceStep) => void;
}): React.ReactElement {
  const voice = catalogPersona(personaId);
  return (
    <Field label="Stack" htmlFor={`step-agent-${step.id}`}>
      <Select
        value={step.agentId ?? DEFAULT_AGENT.id}
        onValueChange={(agentId) => onChange({ ...step, agentId })}
      >
        <SelectTrigger id={`step-agent-${step.id}`} className="w-80">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {AGENTS.map((a) => {
            const realization = voice ? realize(voice, a) : null;
            const ok = realization?.ok ?? false;
            return (
              <SelectItem key={a.id} value={a.id} disabled={!ok}>
                {a.displayName}
                {ok ? '' : ' — cannot speak as this voice'}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      <Badge variant="outline">VagusX ops</Badge>
    </Field>
  );
}
